import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  Image as ImageIcon,
  LockKey,
  PaperPlaneTilt,
  PencilSimpleLine,
  Sparkle,
  SquaresFour,
} from "@phosphor-icons/react";
import connectionTriptych from "./assets/connection-triptych-v1.jpg";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const connectionStories = [
  {
    quote: "The tiny joke that only one person needs to understand.",
    people: "Best friends",
    position: "left",
    alt: "Two best friends sharing a quiet laugh at home",
  },
  {
    quote: "A quiet check-in that doesn’t need another group chat.",
    people: "Family",
    position: "center",
    alt: "An adult child and older parent spending time together",
  },
  {
    quote: "The sketch, reference, or reminder your favourite collaborator needs next.",
    people: "Creative partners",
    position: "right",
    alt: "Two creative collaborators reviewing sketches in a studio",
  },
];

function Mark({ compact = false }) {
  return (
    <span className={`mark ${compact ? "mark--compact" : ""}`} aria-hidden="true">
      <svg viewBox="0 0 128 128">
        <path d="M64 64C54 47 44 39 34 44c-16 7-13 35 5 40 11 3 19-9 25-20 6-10 14-22 25-19 18 5 21 32 5 39-10 5-20-3-30-20Z" />
        <circle cx="64" cy="64" r="4" />
      </svg>
    </span>
  );
}

const legalPages = {
  "/privacy": {
    eyebrow: "Your privacy",
    title: "Privacy Policy",
    summary: "How Cupled handles the information needed to connect two people and deliver private canvases to their widgets.",
  },
  "/terms": {
    eyebrow: "Using Cupled",
    title: "Terms of Use",
    summary: "The ground rules for using Cupled thoughtfully, safely, and with respect for the person you connect with.",
  },
  "/support": {
    eyebrow: "We’re here to help",
    title: "Support",
    summary: "Questions, account concerns, safety issues, or something not working as expected? Get in touch.",
  },
};

function LegalNav() {
  return (
    <header className="nav-shell">
      <nav className="nav legal-nav" aria-label="Legal navigation">
        <a className="wordmark" href="/" aria-label="Cupled home">
          <Mark compact />
          <span>Cupled</span>
        </a>
        <div className="legal-nav__links">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/support">Support</a>
        </div>
        <a className="nav-cta" href="/">Back to Cupled <ArrowUpRight weight="bold" /></a>
      </nav>
    </header>
  );
}

function LegalFooter() {
  return (
    <footer>
      <a className="wordmark" href="/"><Mark compact /><span>Cupled</span></a>
      <p>Made for the people who matter to you.</p>
      <div className="footer-links"><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/support">Support</a></div>
      <span className="copyright">© {new Date().getFullYear()} Cupled</span>
    </footer>
  );
}

function PrivacyContent() {
  return (
    <>
      <section>
        <h2>What Cupled collects</h2>
        <p>When you use Cupled, we process account information such as your display name, email address, account identifiers, and profile image supplied through sign-in. We also process connection records, invite codes and their status, and timestamps needed to link you with one other person.</p>
        <p>Content you choose to create or send—including titles, text notes, emoji, stickers, photos, drawings, layouts, and rendered widget snapshots—is stored so Cupled can deliver it to your connection and show your shared history. We also process an iOS push token, its sandbox or production environment, and your alert preference to deliver generic notifications and background widget refreshes.</p>
        <p>If you contact us, we receive the information you include in your support or privacy message. Please do not send passwords, sign-in codes, or private canvas content.</p>
      </section>
      <section>
        <h2>How information is used</h2>
        <p>We use this information to authenticate accounts, create and manage connections, store and deliver canvases, render widgets, send notifications, prevent misuse, enforce storage limits, and respond to support or account-deletion requests.</p>
        <p>Cupled does not run advertising or third-party analytics, use your information for cross-app tracking, or sell personal information. Your content is not a public feed. Your display name, profile image, and shared content are made available only to you and the person in your active Cupled connection as needed to provide the service.</p>
      </section>
      <section>
        <h2>Service providers</h2>
        <p>Cupled relies on service providers to operate the app: Clerk provides authentication; Convex provides backend compute, databases, and file storage; and Apple provides Sign in with Apple, push notifications, App Store distribution, and device services. These providers process information under their own terms and privacy policies and may process it in countries where they operate.</p>
      </section>
      <section>
        <h2>On your device</h2>
        <p>Cupled keeps your current unsent draft in the app’s private storage and an account-scoped copy of the latest received canvas in the shared App Group used by the widget. Preferences such as widget setup, notification state, and the current push token may also be stored locally.</p>
        <p>The system photo picker gives Cupled only the image you select; Cupled does not request broad access to your photo library. Copying an invite writes that code to the clipboard only when you tap Copy. You control notification permission in iOS Settings.</p>
      </section>
      <section>
        <h2>Storage and retention</h2>
        <p>Cupled retains up to the newest 50 published canvases for an active connection. Media limits of 300 referenced files or 60 MiB can remove older canvases sooner, while each person’s newest received canvas is protected. Temporary or unattached uploads are cleaned up after their short processing and grace periods.</p>
        <p>Invite codes expire after seven days. An expired invite record may remain as account metadata until the related account or connection is deleted. Disconnecting immediately revokes access and schedules deletion of the former connection, its invite, shared history, snapshots, and media.</p>
        <p>The on-device draft and widget cache are cleared when you sign out, disconnect, or delete your account. Removing the app also removes its local data under normal iOS behavior.</p>
      </section>
      <section>
        <h2>Your choices</h2>
        <p>You can edit your display name, disconnect from another person, or initiate permanent account deletion from Settings in the app. Deletion signs you out and removes your profile, connection, invites, devices, shared content, media, and any purchase-entitlement records in a background cleanup process.</p>
        <p>After account deletion, Cupled retains a one-way SHA-256 digest of the stable sign-in identifier together with deletion request and completion timestamps. This permanent suppression record contains no profile fields, account ID, connection ID, or user content; it prevents the deleted identity from being recreated and lets the deletion process be retried or reported safely.</p>
        <p>You can also contact us to ask a privacy question or request access, correction, or deletion help. We may need to verify that a request relates to your account before acting on it.</p>
      </section>
      <section>
        <h2>Security and age</h2>
        <p>We use access controls and authenticated requests designed to keep content within the intended connection. No online service can guarantee absolute security, and Cupled does not claim that content is end-to-end encrypted.</p>
        <p>Cupled is not directed to children under 13. If you believe a child has provided personal information, contact us so we can investigate and take appropriate action.</p>
      </section>
      <section>
        <h2>Changes and contact</h2>
        <p>We may update this policy as the product or its providers change. Material updates will be reflected here with a new effective date.</p>
        <p>Email privacy questions to <a href="mailto:hello@cupled.app?subject=Cupled%20privacy">hello@cupled.app</a>.</p>
      </section>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <section>
        <h2>Agreement</h2>
        <p>By creating an account or using Cupled, you agree to these terms and acknowledge the Privacy Policy. If you do not agree, do not use the service. You must be at least 13 and legally able to agree to these terms.</p>
      </section>
      <section>
        <h2>Your account and connection</h2>
        <p>Keep your account and device secure, provide accurate information, and tell us promptly if you suspect unauthorized access. Cupled supports one active private connection per account. Invite codes are intended for one recipient and expire after seven days; do not post them publicly.</p>
        <p>A Cupled connection gives each participant access to the other person’s display name, profile image, and shared content while the connection is active. Either person can disconnect at any time. Disconnecting revokes access for both people and permanently schedules deletion of the connection and its shared history and media.</p>
      </section>
      <section>
        <h2>Your content</h2>
        <p>You retain ownership of content you create. You give Cupled a limited permission to host, process, reproduce, and deliver that content only as needed to operate, secure, and support the service. Only share content you have the right and consent to share.</p>
        <p>Sending a canvas makes it available to the person in your connection and can place a rendered copy on their widgets. Older canvases may be removed automatically under Cupled’s storage limits. Disconnecting or deleting an account makes shared content unavailable and starts background cleanup; temporary copies may remain while that process completes.</p>
      </section>
      <section>
        <h2>Acceptable use</h2>
        <p>Do not use Cupled to threaten, harass, exploit, impersonate, stalk, deceive, or violate another person’s privacy or rights. Do not upload illegal or malicious material, attempt to bypass access controls, disrupt the service, probe for vulnerabilities without permission, or use automated access that burdens the service.</p>
      </section>
      <section>
        <h2>Availability and changes</h2>
        <p>Cupled may change, suspend, or discontinue features and may enforce reasonable limits needed for security, reliability, or storage. We may restrict or terminate access when necessary to address misuse, legal obligations, or risk to Cupled or others.</p>
        <p>Apple, Clerk, and Convex services are governed by their own terms and availability. Notification and widget delivery can be delayed or limited by network conditions, device settings, and iOS.</p>
      </section>
      <section>
        <h2>Disclaimers and responsibility</h2>
        <p>Cupled is provided on an “as is” and “as available” basis to the extent permitted by law. We do not promise uninterrupted operation, permanent storage, or that every notification or widget update will arrive immediately. Keep copies of content you cannot afford to lose.</p>
        <p>Nothing in these terms excludes rights or remedies that cannot legally be excluded. To the extent permitted by law, Cupled is not responsible for indirect, incidental, special, consequential, or punitive loss arising from use of the service.</p>
      </section>
      <section>
        <h2>Contact</h2>
        <p>Questions about these terms can be sent to <a href="mailto:hello@cupled.app?subject=Cupled%20terms">hello@cupled.app</a>.</p>
      </section>
    </>
  );
}

function SupportContent() {
  return (
    <>
      <section>
        <h2>Connect and create</h2>
        <p>Each account can have one active connection. Share an invite code with one trusted person; codes expire after seven days. Once connected, use Create to add text, emoji, stickers, a selected photo, or a drawing to the Home Screen canvas. The Lock Screen canvas supports one short text note.</p>
      </section>
      <section>
        <h2>Add the widgets</h2>
        <p>Touch and hold the iPhone Home Screen or Lock Screen, choose to add or edit widgets, then select Cupled. Add both widget types if you want the full canvas and short Lock Screen note. iOS controls widget refresh timing, so a newly delivered canvas may not appear immediately.</p>
      </section>
      <section>
        <h2>Notifications</h2>
        <p>Canvas alerts can be enabled from Cupled Settings. Turning alert banners off does not disable signed-in background widget delivery. To change permission after your first choice, use the Cupled page in iOS Settings.</p>
      </section>
      <section>
        <h2>Contact support</h2>
        <p>Email <a href="mailto:hello@cupled.app?subject=Cupled%20support">hello@cupled.app</a>. Include a short description of what happened, the Cupled version and build shown in Settings, your iOS version, and the steps that led to the problem. Do not email passwords, sign-in codes, private keys, invite codes, or private canvas content.</p>
        <a className="button button--light legal-button" href="mailto:hello@cupled.app?subject=Cupled%20support">Email support <ArrowUpRight weight="bold" /></a>
      </section>
      <section>
        <h2>Account and safety concerns</h2>
        <p>If you believe someone has accessed your account, shared harmful content, or used Cupled to harass you, disconnect in the app when it is safe to do so and contact us with the subject “Safety.” For immediate danger, contact local emergency services.</p>
      </section>
      <section>
        <h2>Account deletion</h2>
        <p>You can permanently delete your account from Settings in the Cupled app. The app signs you out immediately while associated account and content data are removed in the background. If you cannot access the app, email support from the address associated with your account and describe the access problem.</p>
      </section>
      <section>
        <h2>Response times</h2>
        <p>We review requests as soon as we can. Safety and account-access reports are prioritized, but response times can vary.</p>
      </section>
    </>
  );
}

function LegalPage({ pageKey }) {
  const page = legalPages[pageKey];

  useEffect(() => {
    document.title = `${page.title} — Cupled`;
    window.scrollTo(0, 0);
  }, [page.title]);

  return (
    <main className="page legal-page" id="top">
      <a className="skip-link" href="#legal-content">Skip to main content</a>
      <LegalNav />
      <header className="legal-hero">
        <p className="legal-eyebrow">{page.eyebrow}</p>
        <h1>{page.title}</h1>
        <p>{page.summary}</p>
        <time dateTime="2026-08-22">{pageKey === "/support" ? "Updated 22 August 2026" : "Effective 22 August 2026"}</time>
      </header>
      <article className="legal-content" id="legal-content">
        {pageKey === "/privacy" && <PrivacyContent />}
        {pageKey === "/terms" && <TermsContent />}
        {pageKey === "/support" && <SupportContent />}
      </article>
      <LegalFooter />
    </main>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return (
    <header className="nav-shell">
      <nav className="nav" aria-label="Primary navigation">
        <a className="wordmark" href="#top" aria-label="Cupled home">
          <Mark compact />
          <span>Cupled</span>
        </a>
        <div className={`nav-links ${open ? "nav-links--open" : ""}`} id="primary-navigation">
          <a href="#experience" onClick={() => setOpen(false)}>How it works</a>
          <a href="#privacy" onClick={() => setOpen(false)}>Privacy</a>
          <a href="#download" onClick={() => setOpen(false)}>Get Cupled</a>
        </div>
        <a className="nav-cta" href="#download">Keep someone close <ArrowUpRight weight="bold" /></a>
        <button
          className={`nav-menu ${open ? "nav-menu--open" : ""}`}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-controls="primary-navigation"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span /><span />
        </button>
      </nav>
    </header>
  );
}

function PhonePreview({ variant, className = "" }) {
  const isEditor = variant === "editor";

  return (
    <div
      className={`phone ${className} phone--${variant}`}
      role="img"
      aria-label={isEditor
        ? "Cupled editor on an iPhone creating a thinking of you message for a widget"
        : "iPhone Lock Screen showing a Cupled widget with a thinking of you message from Jamie"}
    >
      <div className="phone-island" />
      {isEditor ? (
        <div className="phone-screen editor-screen">
          <div className="mock-status"><span>9:41</span><span>●●●</span></div>
          <div className="mock-title"><span>Cancel</span><strong>Create</strong><span>•••</span></div>
          <div className="family-picker"><span className="active">Home Screen</span><span>Lock Screen</span></div>
          <div className="editor-canvas">
            <span className="editor-date">MON 10</span>
            <span className="editor-emoji" aria-hidden="true">💭</span>
            <strong>thinking<br />of you</strong>
            <span className="selection-handle selection-handle--one" />
            <span className="selection-handle selection-handle--two" />
          </div>
          <div className="editor-tools"><span>Text</span><span>Photo</span><span>Draw</span></div>
          <div className="send-canvas">Send canvas <PaperPlaneTilt weight="fill" /></div>
        </div>
      ) : (
        <div className="phone-screen lock-screen">
          <div className="lock-date">Monday, 10 August</div>
          <div className="lock-time">9:41</div>
          <div className="lock-widget">
            <span className="lock-widget__icon"><Sparkle weight="fill" /></span>
            <span><strong>thinking of you</strong><small>Jamie · just now</small></span>
          </div>
          <div className="lock-hint">Cupled</div>
        </div>
      )}
    </div>
  );
}

function HomePage() {
  const root = useRef(null);
  const [storyIndex, setStoryIndex] = useState(0);

  useLayoutEffect(() => {
    document.documentElement.classList.add("js");
    return () => document.documentElement.classList.remove("js");
  }, []);

  useGSAP(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;

    const intro = gsap.timeline({ defaults: { ease: "power3.out" } });
    intro
      .from(".nav-shell", { y: -40, opacity: 0, duration: 0.9 })
      .from(".hero-title .line > span", { yPercent: 110, duration: 1.1, stagger: 0.1 }, "-=0.45")
      .from(".hero-copy, .hero-actions, .hero-loop", { y: 28, opacity: 0, duration: 0.8, stagger: 0.1 }, "-=0.65")
      .from(".hero-stage", { y: 70, scale: 0.94, opacity: 0, duration: 1.2 }, "-=0.5");

    gsap.to(".hero-stage", {
      yPercent: 12,
      ease: "none",
      scrollTrigger: { trigger: ".hero", start: "top top", end: "bottom top", scrub: 1 },
    });

    gsap.utils.toArray("[data-reveal]").forEach((element) => {
      gsap.from(element, {
        y: 64,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: { trigger: element, start: "top 84%", once: true },
      });
    });

  }, { scope: root });

  const nextStory = (direction) => {
    setStoryIndex((current) => (current + direction + connectionStories.length) % connectionStories.length);
  };

  const activeStory = connectionStories[storyIndex];

  return (
    <main className="page overflow-x-hidden w-full max-w-full" ref={root} id="top">
      <a className="skip-link" href="#experience">Skip to main content</a>
      <Nav />

      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-ambient" />
        <h1 className="hero-title max-w-6xl w-full" id="hero-title">
          <span className="line"><span>Something from them.</span></span>
          <span className="line"><span>Right on your screen.</span></span>
        </h1>
        <p className="hero-copy">Send a note, doodle, or photo to one person. It appears on their Home Screen or Lock Screen widget—without getting lost in a chat.</p>
        <div className="hero-actions">
          <a className="button button--light" href="#download">Get Cupled <ArrowUpRight weight="bold" /></a>
          <a className="button button--glass" href="#experience">See how it works <ArrowDown weight="bold" /></a>
        </div>
        <ol className="hero-loop" aria-label="How Cupled works">
          <li>Create</li>
          <li>Send</li>
          <li>Widget</li>
        </ol>
        <div className="hero-stage" aria-label="Preview of Cupled on iPhone">
          <div className="hero-orbit hero-orbit--one" />
          <div className="hero-orbit hero-orbit--two" />
          <p className="stage-note stage-note--left">Create it here.<br />See it there.</p>
          <PhonePreview variant="editor" className="phone--hero" />
          <div className="floating-canvas floating-canvas--right" aria-label="Home Screen widget preview showing thinking of you">
            <span className="floating-canvas__time">Home Screen widget</span>
            <span className="floating-canvas__emoji" aria-hidden="true">💭</span>
            <span className="floating-canvas__script">thinking<br />of you</span>
            <span className="floating-canvas__from">from Jamie · just now</span>
          </div>
        </div>
      </section>

      <section className="marquee" aria-label="What you can share">
        <div className="marquee-track">
          {[0, 1, 2].map((row) => (
            <div className="marquee-set" aria-hidden={row > 0} key={row}>
              <span>Notes</span><Sparkle weight="fill" /><span>Doodles</span><ImageIcon weight="fill" /><span>Photos</span><PaperPlaneTilt weight="fill" /><span>Widgets</span><SquaresFour weight="fill" />
            </div>
          ))}
        </div>
      </section>

      <section className="interest" id="experience">
        <div className="section-intro" data-reveal>
          <h2>More feeling than a text. <span className="inline-image inline-image--doodle" role="img" aria-label="A colourful doodle" /> Less noise than a feed.</h2>
        </div>

        <div className="bento grid-flow-dense" data-reveal>
          <article className="bento-card bento-card--canvas group">
            <div className="bento-copy">
              <PencilSimpleLine weight="duotone" />
              <h3>Make it yours</h3>
              <p>Layer words, colour, photos, and freehand marks into a canvas that sounds like you.</p>
            </div>
            <div className="canvas-art group-hover-scale">
              <span className="canvas-art__date">MON 10</span>
              <span className="canvas-art__emoji" aria-hidden="true">✨</span>
              <span className="canvas-art__line">You’ve got this.</span>
              <span className="canvas-art__scribble" />
            </div>
          </article>

          <article className="bento-card bento-card--widget group">
            <div className="widget-preview group-hover-scale">
              <span>9:41</span>
              <div className="widget-tile"><span className="widget-tile__emoji" aria-hidden="true">☀️</span><span>today’s mood</span><strong>Golden</strong></div>
            </div>
            <div className="bento-copy">
              <SquaresFour weight="duotone" />
              <h3>See it, naturally</h3>
              <p>Their latest canvas can sit right on your widgets.</p>
            </div>
          </article>

          <article className="bento-card bento-card--privacy group" id="privacy">
            <div className="privacy-rings group-hover-scale" aria-hidden="true">
              <span className="privacy-orbit privacy-orbit--outer" />
              <span className="privacy-orbit privacy-orbit--middle" />
              <span className="privacy-orbit privacy-orbit--inner" />
              <span className="privacy-lock"><LockKey weight="duotone" /></span>
              <span className="privacy-chip privacy-chip--one">Only you two</span>
              <span className="privacy-chip privacy-chip--two">No audience</span>
            </div>
            <div className="bento-copy">
              <LockKey weight="duotone" />
              <h3>Private connection</h3>
              <p>One shared space for two people. Disconnect whenever you choose.</p>
            </div>
          </article>

          <article className="bento-card bento-card--connection group">
            <div className="bento-copy">
              <PaperPlaneTilt weight="duotone" />
              <h3>Close means whatever it means to you</h3>
              <p>A partner, a sibling, a best friend, a parent, or the collaborator who always gets it.</p>
              <div className="connection-checks"><span><Check weight="bold" /> No public profile</span><span><Check weight="bold" /> No follower count</span></div>
            </div>
            <div className="connection-portrait group-hover-scale">
              <img src={connectionTriptych} alt="An adult child and older parent sharing an everyday moment at home" width="2048" height="683" loading="lazy" decoding="async" />
            </div>
          </article>
        </div>
      </section>

      <section className="product-moment">
        <div className="product-copy" data-reveal>
          <h2>From your canvas to their Lock Screen.</h2>
          <p>The latest thing you send appears on their Home Screen or Lock Screen, ready for the next glance.</p>
        </div>
        <div className="product-visual" data-reveal>
          <PhonePreview variant="lockscreen" className="phone--product" />
          <div className="product-note product-note--one"><span>sent privately</span><strong>from one person</strong></div>
          <div className="product-note product-note--two"><span>lock screen</span><strong>there at a glance</strong></div>
        </div>
      </section>

      <section className="carousel-section">
        <div className={`carousel-image carousel-image--${activeStory.position}`} key={activeStory.position}>
          <img src={connectionTriptych} alt={activeStory.alt} width="2048" height="683" loading="lazy" decoding="async" />
        </div>
        <div className="carousel-copy">
          <blockquote id="connection-story" aria-live="polite">“{activeStory.quote}”</blockquote>
          <p className="carousel-person">Made with {activeStory.people.toLowerCase()} in mind</p>
          <div className="carousel-controls">
            <button onClick={() => nextStory(-1)} aria-label="Previous connection story" aria-controls="connection-story"><ArrowLeft weight="bold" /></button>
            <span aria-label={`Story ${storyIndex + 1} of ${connectionStories.length}`}>{String(storyIndex + 1).padStart(2, "0")} / {String(connectionStories.length).padStart(2, "0")}</span>
            <button onClick={() => nextStory(1)} aria-label="Next connection story" aria-controls="connection-story"><ArrowRight weight="bold" /></button>
          </div>
        </div>
      </section>

      <section className="final-cta" id="download">
        <div className="final-glow" />
        <Mark />
        <h2>Make their next glance feel like something.</h2>
        <p>Cupled is being prepared for its first release. A more personal corner of your phone is on the way.</p>
        <a className="button button--light button--large" href="mailto:hello@cupled.app?subject=Cupled%20launch%20updates">Get launch updates <ArrowUpRight weight="bold" /></a>
      </section>

      <footer>
        <a className="wordmark" href="#top"><Mark compact /><span>Cupled</span></a>
        <p>Made for the people who matter to you.</p>
        <div className="footer-links"><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/support">Support</a><a href="#top">Back to top</a></div>
        <span className="copyright">© {new Date().getFullYear()} Cupled</span>
      </footer>
    </main>
  );
}

function App() {
  const pageKey = window.location.pathname.replace(/\/$/, "") || "/";
  return legalPages[pageKey] ? <LegalPage pageKey={pageKey} /> : <HomePage />;
}

export default App;
